const BLOCKED_UA_SUBSTRINGS = [
  'msie',
  'trident',
  'arc',
  'perplexity',
  'atlas',
  'chatgpt',
];

const DEVICE_LABEL_BLACKLIST = [
  'obs',
  'manycam',
  'snap camera',
  'logi capture',
  'droidcam',
  'unity video',
  'camo',
  'epoccam',
];

const DEFAULT_CAPTURE_SETTINGS = {
  screenWidth: 1024,
  cameraWidth: 640,
  screenQuality: 0.8,
  cameraQuality: 0.8,
  screenIntervalMs: 10_000,
  focusLossDelayMs: 1_000,
  focusLossIntervalMs: 5_000,
  audioChunkMs: 10_000,
  audioBitsPerSecond: 24_000,
};

export function isBlockedBrowser(userAgent = navigator.userAgent) {
  const ua = (userAgent || '').toLowerCase();
  if (BLOCKED_UA_SUBSTRINGS.some((item) => ua.includes(item))) {
    return {blocked: true, reason: 'blocked_user_agent'};
  }

  const isSafari = ua.includes('safari')
    && !ua.includes('chrome')
    && !ua.includes('crios')
    && !ua.includes('chromium')
    && !ua.includes('edg')
    && !ua.includes('opr');
  if (isSafari) {
    return {blocked: true, reason: 'safari_not_supported'};
  }

  return {blocked: false, reason: ''};
}

export function checkBrowserSupport() {
  if (!navigator?.mediaDevices) {
    return {ok: false, reason: 'media_devices_missing'};
  }
  if (typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    return {ok: false, reason: 'get_display_media_missing'};
  }
  if (typeof window.MediaRecorder !== 'function') {
    return {ok: false, reason: 'media_recorder_missing'};
  }
  if (typeof HTMLCanvasElement?.prototype?.toBlob !== 'function') {
    return {ok: false, reason: 'canvas_to_blob_missing'};
  }
  return {ok: true, reason: ''};
}

export async function checkMultiScreen() {
  if (typeof window.getScreenDetails !== 'function') {
    if (typeof window.screen?.isExtended === 'boolean') {
      const isExtended = window.screen.isExtended;
      if (isExtended) {
        return {ok: false, reason: 'multi_screen_detected', screensCount: 0, isExtended};
      }
      return {ok: true, reason: '', screensCount: 0, isExtended};
    }
    return {ok: false, reason: 'screen_details_missing'};
  }
  try {
    const details = await window.getScreenDetails();
    const screensCount = details?.screens?.length || 0;
    const isExtended = Boolean(details?.isExtended);
    if (isExtended || screensCount > 1) {
      return {ok: false, reason: 'multi_screen_detected', screensCount, isExtended};
    }
    return {ok: true, reason: '', screensCount, isExtended};
  } catch (error) {
    return {ok: false, reason: 'screen_details_error'};
  }
}

export async function checkDeviceList(blacklist = DEVICE_LABEL_BLACKLIST) {
  if (!navigator?.mediaDevices?.enumerateDevices) {
    return {ok: false, reason: 'enumerate_devices_missing', blocked: []};
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  const blocked = [];
  for (const device of devices) {
    if (device.kind !== 'videoinput' || !device.label) {
      continue;
    }
    const label = device.label.toLowerCase();
    if (blacklist.some((item) => label.includes(item))) {
      blocked.push(device.label);
    }
  }
  return {ok: blocked.length === 0, blocked};
}

export async function startScreenCapture({onEnded} = {}) {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      displaySurface: 'monitor',
      preferCurrentTab: false,
      selfBrowserSurface: 'exclude',
      monitorTypeSurfaces: 'include',
    },
  });
  const track = stream.getVideoTracks()[0];
  if (track) {
    if (typeof onEnded === 'function') {
      track.addEventListener('ended', onEnded);
    }
    const settings = track.getSettings();
    if (settings?.displaySurface && settings.displaySurface !== 'monitor') {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error('display_surface_not_monitor');
    }
  }
  return {stream, track};
}

export async function startCameraCapture() {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {width: {ideal: 1280}},
  });
  const track = stream.getVideoTracks()[0];
  return {stream, track};
}

function getAudioMimeType() {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const mimeType of candidates) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return '';
}

export async function startAudioCapture({onData} = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({audio: true});
  const mimeType = getAudioMimeType();
  const options = {
    audioBitsPerSecond: DEFAULT_CAPTURE_SETTINGS.audioBitsPerSecond,
  };
  if (mimeType) {
    options.mimeType = mimeType;
  }
  const recorder = new MediaRecorder(stream, options);
  if (typeof onData === 'function') {
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data && event.data.size > 0) {
        onData(event.data);
      }
    });
  }
  return {stream, recorder, mimeType};
}

export function captureFrame(video, targetWidth, quality = 0.8) {
  if (!video?.videoWidth || !video?.videoHeight) {
    throw new Error('video_not_ready');
  }
  const scale = Math.min(1, targetWidth / video.videoWidth);
  const width = Math.round(video.videoWidth * scale);
  const height = Math.round(video.videoHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(video, 0, 0, width, height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', quality);
  });
}

async function presignUpload({baseUrl, examId, userId, type, jwt, ts}) {
  const url = `${baseUrl}/presign/${examId}/${userId}/${type}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ts}),
  });
  if (!response.ok) {
    throw new Error(`presign_failed_${response.status}`);
  }
  return response.json();
}

async function putBlob(uploadUrl, blob, contentType) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {'Content-Type': contentType},
    body: blob,
  });
  if (!response.ok) {
    throw new Error(`upload_failed_${response.status}`);
  }
}

export async function uploadBlob(params) {
  const {baseUrl, examId, userId, type, jwt, blob, ts} = params;
  const presign = await presignUpload({baseUrl, examId, userId, type, jwt, ts});
  const contentType = presign.content_type;

  try {
    await putBlob(presign.upload_url, blob, contentType);
  } catch (error) {
    if (error?.name === 'TypeError') {
      await putBlob(presign.upload_url, blob, contentType);
    } else {
      throw error;
    }
  }

  return {
    bucket: presign.bucket,
    key: presign.key,
    uploadUrl: presign.upload_url,
    contentType,
  };
}

export function scheduleScreenShots({
  captureFn,
  intervalMs = DEFAULT_CAPTURE_SETTINGS.screenIntervalMs,
  focusLossDelayMs = DEFAULT_CAPTURE_SETTINGS.focusLossDelayMs,
  focusLossIntervalMs = DEFAULT_CAPTURE_SETTINGS.focusLossIntervalMs,
  onError,
} = {}) {
  if (typeof captureFn !== 'function') {
    throw new Error('captureFn_required');
  }

  let baseInterval = null;
  let focusInterval = null;
  let focusDelay = null;

  const safeCapture = () => {
    try {
      const res = captureFn();
      if (res && typeof res.catch === 'function') {
        res.catch((err) => {
          if (typeof onError === 'function') {
            onError(err);
          }
        });
      }
    } catch (err) {
      if (typeof onError === 'function') {
        onError(err);
      }
    }
  };

  const startBase = () => {
    if (!baseInterval) {
      baseInterval = setInterval(safeCapture, intervalMs);
    }
  };

  const stopBase = () => {
    if (baseInterval) {
      clearInterval(baseInterval);
      baseInterval = null;
    }
  };

  const startFocusLoss = () => {
    if (!focusDelay) {
      focusDelay = setTimeout(() => {
        safeCapture();
        focusDelay = null;
      }, focusLossDelayMs);
    }
    if (!focusInterval) {
      focusInterval = setInterval(safeCapture, focusLossIntervalMs);
    }
  };

  const stopFocusLoss = () => {
    if (focusDelay) {
      clearTimeout(focusDelay);
      focusDelay = null;
    }
    if (focusInterval) {
      clearInterval(focusInterval);
      focusInterval = null;
    }
  };

  const handleBlur = () => {
    stopBase();
    startFocusLoss();
  };

  const handleFocus = () => {
    stopFocusLoss();
    startBase();
  };

  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') {
      handleBlur();
    } else {
      handleFocus();
    }
  };

  startBase();
  window.addEventListener('blur', handleBlur);
  window.addEventListener('focus', handleFocus);
  document.addEventListener('visibilitychange', handleVisibility);

  return {
    stop() {
      stopBase();
      stopFocusLoss();
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    },
  };
}

function stopStream(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

function stopRecorder(recorder) {
  if (!recorder || recorder.state === 'inactive') {
    return;
  }
  recorder.stop();
}

export function stopProctoring(state = {}) {
  if (state.screenSchedule?.stop) {
    state.screenSchedule.stop();
  }
  if (state.cameraSchedule?.stop) {
    state.cameraSchedule.stop();
  }
  stopRecorder(state.audioRecorder);
  stopStream(state.screenStream);
  stopStream(state.cameraStream);
  stopStream(state.audioStream);
}

export const PROCTORING_DEFAULTS = DEFAULT_CAPTURE_SETTINGS;
export const PROCTORING_DEVICE_BLACKLIST = DEVICE_LABEL_BLACKLIST;
